import React, { useMemo } from 'react';
import { View, Pressable, StyleSheet, Text } from 'react-native';
import { useTranslation } from 'react-i18next';
import { PantryIcon } from './PantryIcon';
import { ZONE_META, ZONE_KEYS } from './zoneMeta';
import { freshnessDaysLabel } from './freshnessLabel';
import { Paprika } from '../../styles/theme';
import { PantryLocation } from '../../utils/pantryLocation';
import { estimateExpiry, daysUntilExpiry } from '../../services/pantryFreshnessService';

/** Enough of a food to date it. Works for a pantry row and for a detected item alike. */
export interface ZonePickerFood {
  name: string;
  category: string;
  opened?: boolean;
}

interface Props {
  food: ZonePickerFood;
  current?: PantryLocation;
  onPick: (zone: PantryLocation) => void;
}

/**
 * Pick where a food is kept — and say what that buys you.
 *
 * The four zones are not interchangeable slots: shrimp keeps two days in the
 * fridge and six months in the freezer, and until the tiles said so the choice
 * looked like filing rather than a decision. It is also the only surface where
 * `buildMoveUpdates` re-dating on a freeze is visible to anyone.
 *
 * Shared by the pantry's Move sheet and the receipt review, so the two cannot
 * drift apart.
 */
export function ZonePicker({ food, current, onPick }: Props) {
  const { t } = useTranslation();

  // Four estimates per open, not four per render: the shelf-life service walks
  // a ~200-row table by name and this sits inside a sheet that re-renders on
  // every parent tick.
  const keepsFor = useMemo(() => {
    const today = new Date();
    return ZONE_KEYS.reduce((acc, zone) => {
      const expiry = estimateExpiry(food.category, zone, today, food.name, food.opened);
      const days = expiry ? daysUntilExpiry({ expiryDate: expiry }, today) : null;
      // '' for a zone that cannot date this food (a frozen item on the shelf).
      // The tile then shows its label alone — never a dash or an empty row.
      acc[zone] = freshnessDaysLabel(t, days, 'fresh');
      return acc;
    }, {} as Record<PantryLocation, string>);
  }, [food.name, food.category, food.opened, t]);

  return (
    <View style={styles.zoneGrid}>
      {ZONE_KEYS.map((zone) => {
        const meta = ZONE_META[zone];
        const isCurrent = zone === current;
        // The food is not going anywhere, so a shelf life for it would be
        // answering a question nobody asked.
        const sub = isCurrent ? t('pantry.item.currentlyHere') : keepsFor[zone];

        return (
          <Pressable
            key={zone}
            disabled={isCurrent}
            accessibilityRole="button"
            accessibilityState={{ disabled: isCurrent }}
            accessibilityLabel={`${t(meta.labelKey)}${sub ? ` · ${sub}` : ''}`}
            testID={`zone-pick-${zone}`}
            style={[styles.zoneButton, isCurrent && styles.zoneButtonDisabled]}
            onPress={() => !isCurrent && onPick(zone)}
          >
            <View style={[styles.zoneIcon, { opacity: isCurrent ? 0.55 : 1 }]}>
              <View style={styles.iconWrapper}>
                <PantryIcon icon={meta.icon} size={20} color={Paprika.textPrimary} />
              </View>
            </View>
            <View style={styles.zoneTextContainer}>
              <Text style={styles.zoneLabel} numberOfLines={1}>{t(meta.labelKey)}</Text>
              {!!sub && <Text style={styles.zoneSub} numberOfLines={1}>{sub}</Text>}
            </View>
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  zoneGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  zoneButton: {
    width: '48%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    paddingVertical: 14,
    paddingHorizontal: 13,
    borderRadius: 14,
    backgroundColor: Paprika.surfaceLowest,
    borderWidth: 1,
    borderColor: Paprika.hairline,
  },
  zoneButtonDisabled: {
    opacity: 0.55,
  },
  zoneIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Paprika.glassWhite,
    flex: 0,
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  zoneTextContainer: {
    minWidth: 0,
    flex: 1,
  },
  zoneLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13.5,
    fontWeight: '600',
    color: Paprika.textPrimary,
  },
  zoneSub: {
    fontFamily: 'Inter-Regular',
    fontSize: 10.5,
    color: Paprika.textMuted,
    marginTop: 2,
  },
});
